import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { http } from "../../services/http";
import { Update, APPNAME_GERACAOXML } from "../utils/react_grid_utils";
import authenticatedUserContext from "../security/AuthenticatedUserContext";
import {
  formatTipoAcomodacao,
  tipoAcomodacaoOptions,
} from "../utils/tipo_acomodacao";

Modal.setAppElement("#root");

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "860px",
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

function parseJsonMaybe(value) {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function formatDateTime(dateTime) {
  if (!dateTime) {
    return "—";
  }
  const date = new Date(dateTime);
  return (
    date.toLocaleDateString("pt-BR") +
    " " +
    date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function tipoAcomodacaoLabel(value) {
  if (value == null || value === "") return "—";
  return formatTipoAcomodacao(value) || String(value);
}

function getDifferences(oldData, newData) {
  const oldObj = parseJsonMaybe(oldData) || {};
  const newObj = parseJsonMaybe(newData) || {};
  const keys = Array.from(
    new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
  );
  const diffs = [];
  keys.forEach((key) => {
    const a = oldObj[key];
    const b = newObj[key];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      diffs.push({ key, old: a, next: b });
    }
  });
  return diffs;
}

export default function MatrizDetailModal({
  open,
  row,
  alert,
  onClose,
  onSaved,
}) {
  const canEdit = authenticatedUserContext.allow_edit(APPNAME_GERACAOXML) === true;
  const [form, setForm] = useState({
    id: null,
    convenio: "",
    plano: "",
    tabela: "",
    aba: "",
    ch: "",
    tipo_acomodacao: "",
  });
  const [convenios, setConvenios] = useState([]);
  const [abas, setAbas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !row) {
      return;
    }
    setForm({
      id: row.id,
      convenio: row.convenio || "",
      plano: row.plano || "",
      tabela: row.tabela || "",
      aba: row.aba || "",
      ch: row.ch != null ? row.ch : "",
      tipo_acomodacao:
        row.tipo_acomodacao != null ? String(row.tipo_acomodacao) : "",
    });
    loadLookups();
    loadHistorico(row.id);
  }, [open, row]);

  function loadLookups() {
    http.get("convenios").then(
      (response) => setConvenios(response?.data || []),
      () => setConvenios([])
    );
    http.get("matriz/abas").then(
      (response) => setAbas(response?.data || []),
      () => setAbas([])
    );
  }

  function loadHistorico(moduleId) {
    setLoadingHistorico(true);
    http
      .get(
        `/history?module_name=matriz&module_id=${encodeURIComponent(
          moduleId
        )}&page=1&limit=50`
      )
      .then(
        (response) => {
          setHistorico(response?.data?.data || []);
        },
        () => setHistorico([])
      )
      .finally(() => setLoadingHistorico(false));
  }

  function onChangeField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function salvar() {
    if (!form.id) {
      return;
    }
    if (!form.convenio || !form.plano) {
      alert.error("Convênio e plano são obrigatórios.");
      return;
    }
    setSaving(true);
    Update(
      "matriz",
      {
        id: form.id,
        convenio: form.convenio,
        plano: form.plano,
        aba: form.aba,
        ch: form.ch,
        tipo_acomodacao: form.tipo_acomodacao,
      },
      alert
    )
      .then((response) => {
        const saved = response?.data?.[0] || form;
        setForm({
          id: saved.id,
          convenio: saved.convenio || "",
          plano: saved.plano || "",
          tabela: saved.tabela || "",
          aba: saved.aba || "",
          ch: saved.ch != null ? saved.ch : "",
          tipo_acomodacao:
            saved.tipo_acomodacao != null ? String(saved.tipo_acomodacao) : "",
        });
        loadHistorico(saved.id);
        if (onSaved) {
          onSaved(saved);
        }
      })
      .finally(() => setSaving(false));
  }

  if (!open || !row) {
    return null;
  }

  return (
    <Modal
      isOpen={open}
      onRequestClose={saving ? undefined : onClose}
      style={modalStyles}
      contentLabel="Detalhe Matriz"
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Matriz #{form.id}</h3>
        <button className="btn btn-default btn-sm" type="button" onClick={onClose} disabled={saving}>
          Fechar
        </button>
      </div>
      <hr />

      <h4 style={{ marginTop: 0 }}>Edição</h4>
      <div className="row">
        <div className="col-sm-6 form-group">
          <label>Convênio</label>
          <select
            className="form-control"
            value={form.convenio}
            disabled={!canEdit || saving}
            onChange={(e) => onChangeField("convenio", e.target.value)}
          >
            <option value="">Selecione</option>
            {convenios.map((item) => (
              <option key={item.convenio} value={item.convenio}>
                {item.convenio}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 form-group">
          <label>Plano</label>
          <input
            className="form-control"
            value={form.plano}
            disabled={!canEdit || saving}
            onChange={(e) => onChangeField("plano", e.target.value)}
          />
        </div>
        <div className="col-sm-4 form-group">
          <label>Aba</label>
          <select
            className="form-control"
            value={form.aba}
            disabled={!canEdit || saving}
            onChange={(e) => onChangeField("aba", e.target.value)}
          >
            <option value="">Selecione</option>
            {abas.map((item) => (
              <option key={item.aba} value={item.aba}>
                {item.aba}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-4 form-group">
          <label>Tabela</label>
          <input className="form-control" value={form.tabela || ""} disabled readOnly />
        </div>
        <div className="col-sm-2 form-group">
          <label>CH</label>
          <input
            className="form-control"
            value={form.ch}
            disabled={!canEdit || saving}
            onChange={(e) => onChangeField("ch", e.target.value)}
          />
        </div>
        <div className="col-sm-2 form-group">
          <label>Acomodação</label>
          <select
            className="form-control"
            value={form.tipo_acomodacao}
            disabled={!canEdit || saving}
            onChange={(e) => onChangeField("tipo_acomodacao", e.target.value)}
          >
            <option value="">—</option>
            {tipoAcomodacaoOptions().map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {canEdit ? (
        <div style={{ textAlign: "right", marginBottom: "16px" }}>
          <button
            className="btn btn-success btn-sm"
            type="button"
            disabled={saving}
            onClick={salvar}
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      ) : null}

      <hr />
      <h4>Histórico</h4>
      {loadingHistorico ? (
        <p>Carregando histórico...</p>
      ) : historico.length === 0 ? (
        <p className="text-muted">Nenhum histórico para este registro.</p>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "320px", overflow: "auto" }}>
          <table className="table table-striped table-condensed table-bordered">
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipo</th>
                <th>Usuário</th>
                <th>Alterações</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((item) => {
                const diffs = getDifferences(item.old_data, item.new_data);
                return (
                  <tr key={item.id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(item.created_at)}
                    </td>
                    <td>{item.type}</td>
                    <td>
                      {item.changer_name || "—"}
                      {item.changer_email ? (
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          {item.changer_email}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {item.type === "CREATE" ? (
                        <span>
                          Criado: {parseJsonMaybe(item.new_data)?.plano || "—"} /{" "}
                          {tipoAcomodacaoLabel(
                            parseJsonMaybe(item.new_data)?.tipo_acomodacao
                          )}
                        </span>
                      ) : item.type === "DELETE" ? (
                        <span>Registro excluído</span>
                      ) : diffs.length === 0 ? (
                        <span className="text-muted">Sem diff de campos</span>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: "16px" }}>
                          {diffs
                            .filter((d) => d.key !== "id")
                            .map((d) => (
                              <li key={d.key}>
                                <strong>{d.key}:</strong>{" "}
                                {d.key === "tipo_acomodacao"
                                  ? `${tipoAcomodacaoLabel(d.old)} → ${tipoAcomodacaoLabel(
                                      d.next
                                    )}`
                                  : `${d.old == null || d.old === "" ? "—" : String(d.old)} → ${
                                      d.next == null || d.next === ""
                                        ? "—"
                                        : String(d.next)
                                    }`}
                              </li>
                            ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
